import { useState, useMemo, useCallback } from 'react';
import { modalManager } from '../utils/modalManager';
import DateRangePicker from '../components/mail/DateRangePicker';
import { useTranslation } from './useTranslation';

export type DateFilter = { from?: Date; to?: Date } | null;

interface UseDateFilterParams<T> {
    items: T[];
    getDateFromItem: (item: T) => string | Date; // Функция для получения даты из элемента
}

interface UseDateFilterReturn<T> {
    filteredItems: T[];
    dateFilter: DateFilter;
    handleSearchByDate: () => void;
    handleClearFilter: () => void;
}

/**
 * Универсальная функция для фильтрации элементов по диапазону дат
 */
const filterItemsByDate = <T,>(
    items: T[],
    dateFilter: DateFilter,
    getDateFromItem: (item: T) => string | Date,
): T[] => {
    if (!dateFilter) {
        return items;
    }

    return items.filter((item) => {
        const itemDate = new Date(getDateFromItem(item));
        itemDate.setHours(0, 0, 0, 0);

        const from = dateFilter.from ? new Date(dateFilter.from) : null;
        const to = dateFilter.to ? new Date(dateFilter.to) : null;

        if (from) from.setHours(0, 0, 0, 0);
        if (to) to.setHours(23, 59, 59, 999);

        // Проверяем попадание в диапазон
        if (from && to) {
            return itemDate >= from && itemDate <= to;
        } else if (from) {
            return itemDate >= from;
        } else if (to) {
            return itemDate <= to;
        }

        return true;
    });
};

/**
 * Универсальный хук для фильтрации элементов по дате
 * @param items - Массив элементов для фильтрации
 * @param getDateFromItem - Функция для получения даты из элемента
 */
export const useDateFilter = <T,>({
    items,
    getDateFromItem,
}: UseDateFilterParams<T>): UseDateFilterReturn<T> => {
    const { t, locale } = useTranslation();
    const [dateFilter, setDateFilter] = useState<DateFilter>(null);

    // Фильтруем элементы по дате
    const filteredItems = useMemo(() => {
        return filterItemsByDate(items, dateFilter, getDateFromItem);
    }, [items, dateFilter, getDateFromItem]);

    const handleSearchByDate = useCallback(() => {
        const handleApply = (from: Date | null, to: Date | null) => {
            if (from || to) {
                setDateFilter({ from: from || undefined, to: to || undefined });
            } else {
                setDateFilter(null);
            }
            modalManager.remove();
        };

        const handleReset = () => {
            setDateFilter(null);
            modalManager.remove();
        };

        modalManager.show({
            title: t.pages.mail.searchByDate,
            content: (
                <DateRangePicker
                    initialFrom={dateFilter?.from || null}
                    initialTo={dateFilter?.to || null}
                    onApply={handleApply}
                    onReset={handleReset}
                />
            ),
            size: 'md',
        });
    }, [dateFilter, t, locale]);

    const handleClearFilter = useCallback(() => {
        setDateFilter(null);
    }, []);

    return {
        filteredItems,
        dateFilter,
        handleSearchByDate,
        handleClearFilter,
    };
};
